import { useEffect, useState } from "react";
import { useT } from "../i18n";
import { euro } from "./index";
import { Postenblatt } from "./Postenblatt";
import { Blattvonunten } from "./Blattvonunten";
import { MerklisteAbschluss, type Abschlussart } from "./MerklisteAbschluss";
import { Sammelliste } from "./Merkliste";
import { ZeileDazu } from "./Merkzeile";
import { useMerkliste, useMerksperre } from "../daten/merkliste";
import { usePreiseUnterwegs } from "../daten/preisfrage";
import { useDarfSchreiben } from "../schale/Sitzung";
import "../stile/merkliste.css";

/* ══════════════════════════════════════════════════════════════════
   Die Merkliste am Telefon — ein runder Knopf unten rechts.

   Luca, 17.09.2026: *„Auf dem Mobile muss die Merkliste quasi als
   FAB-Button sein."*

   ── Warum nicht dieselbe Leiste wie am Schirm ─────────────────────

   Am Schreibtisch ist die Merkliste eine Leiste unten rechts: Zahl,
   Wort, Betrag, Pfeil — und aufgeklappt eine Liste, die daneben Platz
   hat. Auf 390 px ist dieselbe Leiste **420 px breit**, also die ganze
   Zeile; sie liegt damit über der Navigationsleiste unten und nimmt der
   Maske die letzten zwei Zeilen. Ein Warenkorb, der die Arbeit verdeckt,
   ist kein Warenkorb.

   Am Telefon steht deshalb ein Knopf: 60 px rund, unten rechts, über
   der Leiste (`env(safe-area-inset-bottom)`), mit der Zahl der Posten
   darauf. Ein Tippen öffnet dieselbe Liste als **Blatt von unten** —
   dieselbe, die jede andere Auswahl am Telefon auch benutzt
   (`Blattvonunten.tsx`), samt Herunterziehen.

   **Leere Liste, kein Knopf.** Ein runder Knopf mit einer Null darauf
   in der Ecke, in der sonst nichts steht, ist ein Ziel, das nichts tut.
   Entschieden wird das eine Ebene höher (`Merkliste.tsx`), damit eine
   leere Merkliste keine einzige Abfrage kostet.

   **Der Abschluss gehört nicht hierher.** „Jetzt vermieten" und
   „Angebot erstellen" öffnen `MerklisteAbschluss` — denselben Weg wie
   am Schreibtisch. Wäre er nicht da, stünde hier ein Knopf ins Leere;
   deshalb wird er aufgerufen und nicht nachgebaut.

   **Und die Karten gehören auch nicht hierher** (18.09.2026). Bis dahin
   baute diese Datei ihre eigene Zeile: Name, volle Angaben als Text,
   Menge, Betrag, Kreuz. Der Zeitraum stand darin als Satz und war damit
   am Telefon nicht zu ändern — Luca: *„Ich kann nur den Zeitraum
   anpassen, wenn ich tatsächlich in den Nachschlagewerken bin."* Jetzt
   steht hier `Sammelliste` aus `Merkliste.tsx`, also dieselbe Karte wie
   in der Spalte und in der Leiste. Was am Telefon anders ist, sind die
   Maße für den Daumen (`stile/merkliste.css`) — nicht die Bedienung.

   Klassen: `mlf-*`, gegen alle Stile geprüft.
   ══════════════════════════════════════════════════════════════════ */

export function MerklisteFab() {
  const t = useT();
  const { posten, wer, summe, stuecke, offene, leeren } = useMerkliste();
  /* Ohne Antwort keine Summe (N12-01): nach „Wie zuletzt" stand sonst eine
     Sekunde lang „Summe netto 0,00 — 2 Zeile(n) ohne Preis". */
  const unterwegs = usePreiseUnterwegs();
  const [offen, setzeOffen] = useState(false);
  const [abschluss, setzeAbschluss] = useState<Abschlussart | null>(null);
  /** Welcher Posten sein Blatt offen hat — `null` heißt keiner. */
  const [blatt, setzeBlatt] = useState<string | null>(null);
  /** Steht das Fenster „nicht frei“ offen, tritt der Knopf zurück (19.09.2026). */
  const sperrt = useMerksperre().length > 0;
  const offenerPosten = posten.find((p) => p.id === blatt) ?? null;
  /* Wer nur liest (P7-03), merkt und rechnet — aber legt nichts an. */
  const schreibt = useDarfSchreiben();

  // Ist die Liste leer, ist auch das Blatt erledigt — sonst stünde ein
  // leeres Blatt da, dessen Knopf darunter gerade verschwunden ist.
  useEffect(() => { if (!posten.length) setzeOffen(false); }, [posten.length]);

  const fertig = () => { setzeAbschluss(null); setzeOffen(false); };

  return (
    <>
      {/* Während der Abschluss läuft, tritt der Knopf zurück: Dort steht
          dieselbe Liste noch einmal, und zwei Zählwerke übereinander
          wären eines zu viel. Und während das Fenster „nicht frei" offen
          ist: Der runde Knopf lag sonst über dessen Satz. */}
      {abschluss || blatt || sperrt ? null : (
        <button
          type="button"
          className="mlf-knopf"
          aria-expanded={offen}
          aria-label={t("merkliste.fabSchild", { zahl: stuecke })}
          onClick={() => setzeOffen(true)}
        >
          <span className="mlf-zahl">{stuecke}</span>
          <span className="mlf-wort">{t("merkliste.titel")}</span>
        </button>
      )}

      <Blattvonunten
        offen={offen && !abschluss && !blatt}
        /* **Hier wird gearbeitet, nicht ausgewählt** — die Regel aus
           `docs/40-mobiles-grundgeruest.md`. Auf dem kleinen Rastpunkt blieben
           dem Leib 111 px: Griff 48, Kopf 58 und der Fuß mit „Jetzt vermieten"
           und „Angebot erstellen" 128 — **kein einziger Posten war ganz zu
           sehen**. Mit `gross` sind es 345 px und vier Posten (gemessen auf
           390 × 844). */
        rast="gross"
        schliessen={() => setzeOffen(false)}
        titel={t("merkliste.titel")}
        unter={stuecke === 1
          ? t("merkliste.einPosten")
          : t("merkliste.posten", { zahl: stuecke })}
        fuss={!schreibt ? null : (
          <>
            {/* Die beiden Wege, die Luca „generell" verlangt hat. Der
                Regelfall trägt die Fläche in Hausgelb (Regel 10:
                Direktvermietung ist der Einstieg, das Angebot der
                Sonderfall). */}
            <button
              type="button"
              className="handy-knopf"
              onClick={() => setzeAbschluss("vermieten")}
            >
              {t("merkliste.vermieten")}
            </button>
            <button
              type="button"
              className="handy-knopf leise"
              onClick={() => setzeAbschluss("angebot")}
            >
              {t("merkliste.angebot")}
            </button>
          </>
        )}
      >
        {/* Für wen — dieselbe Zeile wie am Schreibtisch. */}
        {wer.nr ? (
          <p className="mlf-wer">
            {t("merkliste.fuerWen", { kunde: wer.name || wer.nr })}
            {wer.ort ? ` ${t("allgemein.trennpunkt")} ${wer.ort}` : ""}
          </p>
        ) : null}

        {/* **Dieselben Zeilen wie am Schreibtisch** — zwei Zeilen hoch, und
            aufgeklappt mit Zeitraumgriff, Mengenschritt und Satzfeld darin
            (`Sammelliste` in `Merkliste.tsx`).

            `data-kein-erstfokus` stand hier und ist seit dem 18.09.2026
            erledigt: Das Blatt von unten fokussiert beim Öffnen kein Feld
            mehr, sondern sich selbst (40-mobiles-grundgeruest.md, Nachtrag
            2). Ein Merker, der nichts mehr verhindert, sagt dem Nächsten
            etwas Falsches über den Baustein darunter. */}
        <div className="mlf-karten">
          <Sammelliste oeffneBlatt={setzeBlatt} />
        </div>

        {/* **Und dieselben vier Knöpfe** (18.09.2026): Leistung, Ware,
            Zuschlag, Freitext. Ein Weg, der am Schreibtisch geht und am
            Telefon nicht, ist zwei Wege — und der am Telefon ist der, an dem
            der Anruf hängt. */}
        <ZeileDazu />

        <div className="mlf-summe">
          <span>{t("merkliste.summe")}</span>
          <b>{unterwegs ? t("allgemein.zahlOffen") : euro(summe)}</b>
        </div>
        {/* Netto und noch nicht zugesagt: Eingefroren wird bei der Annahme
            (Regel 4). Steht eine Zeile ohne Betrag darin, sagt es die
            Summe — sonst wäre sie kleiner als die Wahrheit. */}
        <p className="mlf-fuss">
          {offene && !unterwegs ? t(offene === 1 ? "merkliste.fussOffenEiner" : "merkliste.fussOffen", { zahl: offene }) : t("merkliste.fuss")}
        </p>
        <button type="button" className="handy-knopf leise" onClick={leeren}>
          {t("merkliste.leeren")}
        </button>
      </Blattvonunten>

      <Postenblatt
        offen={offenerPosten !== null}
        posten={offenerPosten}
        kunde={wer.nr}
        schliessen={() => setzeBlatt(null)}
      />

      <MerklisteAbschluss
        offen={abschluss !== null}
        art={abschluss ?? "vermieten"}
        schliessen={() => setzeAbschluss(null)}
        fertig={fertig}
      />
    </>
  );
}
